import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, Optional, Inject, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { AccountService } from 'src/app/services/account.service';
import { AlertService } from 'src/app/services/alert.service';
import { UserService } from 'src/app/services/user.service';
import { KilnService } from 'src/app/services/kiln.service';
import { AlertController, NavController, PickerController, Platform, IonSelect } from '@ionic/angular';
import { PushNotificationService } from 'src/app/services/push-notification.service';
import { ToggleService } from 'src/app/services/toggle.service';

import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'app-account-settings-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
})
export class AccountSettingsUsersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() small_screen = false;
  @ViewChild('userSelect', { static: false }) userSelect: IonSelect;

  private resizeSub: any;

  @Output() pending = new EventEmitter<Object>();
  @Output() buttonText = new EventEmitter<Object>();

  public usersForm: UntypedFormGroup;
  public phonesForm: UntypedFormGroup;
  public emailForm: UntypedFormGroup;
  public kilnSecurityForm: UntypedFormGroup;

  public selected_tab = 'details';
  public selected_user = null;
  public selected_index = null;
  public updatePending = false;

  public pushDeviceName = '';
  public pushRegistering = false;
  public otherUserPushDevices: Array<{ id: number; name?: string; platform?: string }> = [];
  public otherUserPushLoading = false;
  public promptPushSending = false;
  /** Whether the current device can register an additional push token for the selected user. */
  public canRegisterAdditionalPushDevice = false;

  public hideAdmins = false;
  public hideBasics = false;
  public hideReadOnly = false;

  public network_devices = [];
  public kilns_by_network_device = {};
  public allowUsers = "all";

  public gettingUserData = false;
  public all_kilns = [];
  public phone_type_options = ['Home', 'Mobile', 'Work'];
  private phoneUtil;
  private loggedInUser;
  private selected_user_email;
  private is_changing_email = false;
  public userInfo = {admin: [], basic: [], readOnly: []};

  roleFilters = {
    admin: true,
    basic: true,
    readonly: true
  };

  selectedRole: 'all' | 'admin' | 'basic' | 'readonly' = 'all';

  /** Whether push notifications feature is enabled (pushNotifications toggle). */
  public showPushNotifications = false;

  /** Whether stop (stop firing) permission is accessible (stop toggle). */
  public showStopToggle = false;

  /** Whether programming permission is accessible (programming toggle). */
  public showProgrammingToggle = false;

  /** True when opened with `add=user`: show create-user form immediately and skip default user selection after load. */
  public createUserFormDuringAccountLoad = false;

  /**
   * Small-screen admin: after loading users, no row is selected until the user picks one or taps Add user.
   * While true, submit is disabled and save() no-ops to avoid accidental creates.
   */
  public smallScreenAwaitingUserPick = false;

  /** Small-screen admin: hide tabs/forms until a user is chosen from the list or Add user is used. */
  get showUserAccountDetailsPanel(): boolean {
    return !this.small_screen || !this.smallScreenAwaitingUserPick;
  }

  /**
   * Small-screen admin: leave the editor and return to the user picker (discards add-user flow or current selection).
   */
  cancelSmallScreenUserEditor(): void {
    if (!this.small_screen || !this.canAdminister || this.smallScreenAwaitingUserPick) {
      return;
    }
    this.selected_user = null;
    this.selected_index = null;
    this.selected_tab = 'details';
    this.smallScreenAwaitingUserPick = true;
  }

  /** KilnLink premium account (phone / push tabs are premium-only). */
  isPremiumAccount(): boolean {
    return this.userService.isPremium();
  }

  /** If account is not premium, leave phone/push/email tabs (not offered in UI). */
  private ensurePremiumTabVisibility(): void {
    if (this.isPremiumAccount()) {
      return;
    }
    if (this.selected_tab === 'phone' || this.selected_tab === 'push' || this.selected_tab === 'email') {
      this.selected_tab = 'details';
    }
  }

  constructor(public accountService: AccountService,
    public alertService: AlertService,
    public userService: UserService,
    public kilnService: KilnService,
    public alertController: AlertController,
    public navCtrl: NavController,
    private pickerController: PickerController,
    private platform: Platform,
    private pushNotificationService: PushNotificationService,
    private toggleService: ToggleService,
    private route: ActivatedRoute,
    private router: Router,
    @Optional() @Inject('accountInformation') public accountInformation: any,
    @Optional() @Inject('canAdminister') public canAdminister: boolean
  ) {
    let login_data = this.userService.getLoginData();
    let loggedInAs = login_data.loggedInAs;
    this.loggedInUser = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? (loggedInAs.email || loggedInAs.username) : (login_data.email || login_data.username);
    this.phoneUtil = PhoneNumberUtil.getInstance();    
  }

  getUserAndDeviceInformation() {
    this.gettingUserData = true;
    this.getAccountUsers();
    this.accountService.getDevicesForAccount().then( (data) => {
      this.network_devices = [];
      this.all_kilns = [];
      this.kilns_by_network_device = {};
      this.network_devices = data && data.network_devices;
      let kilns = data && data.kilns.length > 0 ? data.kilns.sort((a, b) => (a.name > b.name) ? 1 : -1) : [];
      this.all_kilns = kilns;

      if(kilns) {
        kilns = kilns.filter((k) => k)
        this.kilns_by_network_device = kilns.reduce((accumulator, val) => {
          let network_device_id = val.network_device_id || '-1';

          if(!val.network_device_id && !accumulator.hasOwnProperty('-1') && !this.network_devices.find((nd) => nd.name === "Not Connected to Network Device")) {
            this.network_devices.push({id: '-1', name: "Not Connected to Network Device", can_stop: false})
          }

          if(accumulator.hasOwnProperty(network_device_id)) {
            accumulator[network_device_id].push(val);
          } else {
            accumulator[network_device_id] = [val];
          }

          return accumulator;
        }, {})
      } else {
        this.kilns_by_network_device = {};
      }

      this.kilnSecurityForm = undefined;
      this.createKilnSecurityForm();
    });
  }

  ngOnInit() {
    this.toggleService.listToggles();
    this.toggleService.togglesLoaded.subscribe(() => {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
      this.showStopToggle = this.toggleService.isToggleEnabled('stop');
      this.showProgrammingToggle = this.toggleService.isToggleEnabled('programming');
      this.ensurePremiumTabVisibility();
    });
    if (this.toggleService.areTogglesLoaded()) {
      this.showPushNotifications = this.toggleService.isToggleEnabled('pushNotifications');
      this.showStopToggle = this.toggleService.isToggleEnabled('stop');
      this.showProgrammingToggle = this.toggleService.isToggleEnabled('programming');
    }
    this.is_changing_email = false;
    this.network_devices = [];
    this.all_kilns = [];
    this.kilns_by_network_device = {};
    let login_data = this.userService.getLoginData();
    let loggedInAs = login_data.loggedInAs;
    this.loggedInUser = (login_data.hasOwnProperty('loggedInAs') && login_data.loggedInAs) ? (loggedInAs.email || loggedInAs.username) : (login_data.email || login_data.username);
    this.gettingUserData = true;
    this.selected_user = null;
    // Detect small screen so user list shows as ion-select when embedded (e.g. via ngComponentOutlet)
    this.updateSmallScreen();
    this.resizeSub = this.platform.resize.subscribe(() => this.updateSmallScreen());

    const openCreateUserFromRoute =
      this.canAdminister && this.urlSaysAddUserIntent();

    if (openCreateUserFromRoute) {
      this.createUserFormDuringAccountLoad = true;
      this.addUser();
    } else {
      this.createForm();
      this.createPhonesForm();
      this.createEmailForm();
    }
    this.ensurePremiumTabVisibility();
    this.getUserAndDeviceInformation();
  }

  /** `add=user` may not appear on this component's ActivatedRoute snapshot when embedded in Account Admin; read from the real URL. */
  private urlSaysAddUserIntent(): boolean {
    try {
      const q = this.router.parseUrl(this.router.url).queryParams['add'];
      return q === 'user';
    } catch {
      return this.route.snapshot.queryParamMap.get('add') === 'user';
    }
  }

  ngOnDestroy() {
    if (this.resizeSub) {
      this.resizeSub.unsubscribe();
    }
  }

  private updateSmallScreen() {
    const w = this.platform?.width() ?? 0;
    const isDevice = this.platform?.is('mobile') || this.platform?.is('tablet') || this.platform?.is('phablet') || this.platform?.is('mobileweb') || this.platform?.is('desktop');
    this.small_screen = w < 875 && !!isDevice;
  }

  ngOnChanges() {
    this.is_changing_email = false;
    this.ensurePremiumTabVisibility();
    this.getUserAndDeviceInformation();
  }

  verificationSuccess() {
    this.getAccountUsers();
    this.pending.emit({is_pending: false,message: "Please check for your device for a code to complete device verification.",success: true});
  }
  verificationFails() {this.pending.emit({is_pending: false,message: "Something went wrong sending your device a verification code",success: false});}

  deviceConfirmSuccess() {
    this.getAccountUsers();
    this.pending.emit({is_pending: false,message: "Your device has been verified",success: true});
  }
  deviceConfirmFails() {
    this.pending.emit({is_pending: false,message: "Your device could not be verified at this time", success: false});
  }

  getAccountUsers() {
    this.accountService.getUsersForAccount()
        .then((response) => {
          this.accountInformation.users = response.sort((a, b) => {
            return (a.email > b.email) ? 1 : -1;
          });
          this.setUpCanAdminister(response);
          this.userInfo = {
            admin: this.admins(),
            basic: this.basicUsers(),
            readOnly: this.readOnlyUsers()
          };

          const keepNewUserForm = this.createUserFormDuringAccountLoad;
          if (keepNewUserForm) {
            this.createUserFormDuringAccountLoad = false;
            this.gettingUserData = false;
            this.clearAddUserQueryParam();
            return;
          }

          if (this.canAdminister) {
            if (this.small_screen) {
              this.selected_user = null;
              this.selected_index = null;
              this.selected_tab = 'details';
              this.smallScreenAwaitingUserPick = true;
            } else {
              const users = this.accountInformation.users;
              const idx = this.indexOfLoggedInUserInList(users);
              const pick = idx >= 0 ? idx : 0;
              this.selectUser(users && users[pick], pick);
            }
          } else {
            let onlySelf = this.accountInformation.users.filter((user) => { return user.email === this.loggedInUser || user.username === this.loggedInUser});
            this.accountInformation.users = onlySelf;
            this.selectUser(onlySelf, 0);
          }

          this.ensurePremiumTabVisibility();
          this.gettingUserData = false;
        })
        .catch((error) => {
          this.gettingUserData = false;
          console.log(`error getting account users: ${error}`)
        })
  }

  /** Drop `add=user` from the URL after the new-user form is established (avoids re-selecting on refresh). */
  private clearAddUserQueryParam(): void {
    if (!this.urlSaysAddUserIntent()) {
      return;
    }
    this.router.navigate(['/account-admin'], {
      queryParams: { add: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Index of the signed-in user in `users` (for default selection when role filter is “all”). */
  private indexOfLoggedInUserInList(users: any[]): number {
    if (!users || !this.loggedInUser) {
      return -1;
    }
    return users.findIndex((u) =>
      u.email ? (u.email === this.loggedInUser || u.mail_to === this.loggedInUser) : (u.username === this.loggedInUser)
    );
  }

  setUpCanAdminister(data) {
    if(this.accountInformation && this.accountInformation.users && this.loggedInUser) {
      let loggedInUserPermissions = this.accountInformation.users.find((u) => {
        return u.email ? (u.email === this.loggedInUser || u.mail_to === this.loggedInUser) : (u.username === this.loggedInUser);
      })

      this.canAdminister = loggedInUserPermissions ? (loggedInUserPermissions.type === 'admin') : false;
    } else {
      this.canAdminister = false;
    }
  }

  private createForm(user?) {
    let emptyFormControl = (default_value?) => { return new UntypedFormControl((default_value === undefined ? '' : default_value), [Validators.required, Validators.minLength(2)]); }
    let emptyFormControlNotRequired = (default_value?) => { return new UntypedFormControl(((default_value === undefined) ? "" : default_value)); }

    this.selected_user_email = user && user.email;
    this.usersForm = new UntypedFormGroup({
      email: emptyFormControl(user && user.email),
      verification_code: new UntypedFormControl(''),
      type: emptyFormControl((user && user.type) || 'basic'),
      first_name: emptyFormControl(user && user.first_name),
      last_name: emptyFormControl(user && user.last_name)
    });

    if(!user) {
      this.buttonText.emit({submitButton: "Create"});
    }
  }

  clearCurrentForm() {
    let getFormToCheck = {
      "details": this.usersForm,
      "phone": this.phonesForm,
      "email": this.emailForm,
      "security": this.kilnSecurityForm
    }

    let currentForm = getFormToCheck[this.selected_tab];
    currentForm.reset();
  }

  private createPhonesForm() {
    let primary = this.getPhoneInformation();
    let secondary = this.getPhoneInformation(true);

    let isPhoneNumber = input => {
      try {
        let ISD_CODES = [93, 355, 213, 1684, 376, 244, 1264, 672, 1268, 54, 374, 297, 61, 43, 994, 1242, 973, 880, 1246, 375, 32, 501, 229, 1441, 975, 591, 387, 267, 55, 246, 1284, 673, 359, 226, 257, 855, 237, 1, 238, 1345, 236, 235, 56, 86, 61, 61, 57, 269, 682, 506, 385, 53, 599, 357, 420, 243, 45, 253, 1767, 1809, 1829, 1849, 670, 593, 20, 503, 240, 291, 372, 251, 500, 298, 679, 358, 33, 689, 241, 220, 995, 49, 233, 350, 30, 299, 1473, 1671, 502, 441481, 224, 245, 592, 509, 504, 852, 36, 354, 91, 62, 98, 964, 353, 441624, 972, 39, 225, 1876, 81, 441534, 962, 7, 254, 686, 383, 965, 996, 856, 371, 961, 266, 231, 218, 423, 370, 352, 853, 389, 261, 265, 60, 960, 223, 356, 692, 222, 230, 262, 52, 691, 373, 377, 976, 382, 1664, 212, 258, 95, 264, 674, 977, 31, 599, 687, 64, 505, 227, 234, 683, 850, 1670, 47, 968, 92, 680, 970, 507, 675, 595, 51, 63, 64, 48, 351, 1787, 1939, 974, 242, 262, 40, 7, 250, 590, 290, 1869, 1758, 590, 508, 1784, 685, 378, 239, 966, 221, 381, 248, 232, 65, 1721, 421, 386, 677, 252, 27, 82, 211, 34, 94, 249, 597, 47, 268, 46, 41, 963, 886, 992, 255, 66, 228, 690, 676, 1868, 216, 90, 993, 1649, 688, 1340, 256, 380, 971, 44, 1, 598, 998, 678, 379, 58, 84, 681, 212, 967, 260, 263],
          //extract numbers from string
          thenum = input.match(/[0-9]+/g).join(""),
          totalnums = thenum.length,
          last10Digits = parseInt(thenum) % 10000000000,
          ISDcode = thenum.substring(0, totalnums - 10);

        //phone numbers are generally of 8 to 16 digits
        if (totalnums >= 8 && totalnums <= 16) {
          if (ISDcode) {
            if (ISD_CODES.includes(parseInt(ISDcode))) {
              return true;
            } else {
              return false;
            }
          } else {
            return true;
          }
        }
      } catch (e) {}

      return false;
    }

    let validatePhoneNumber = (num) => {
      if(num && num.value && num.value !== "") {
        try {
          if(!num.value.includes("+")) { num.value = `+${num.value}`}
          const number = this.phoneUtil.parseAndKeepRawInput(num.value);
          return this.phoneUtil.isValidNumber(number);
        } catch(e) {
          // console.log(`error formatting phone number: ${JSON.stringify(e)}`)
          return {invalidNumber: {value: num.value}};
        }
      } else {
        return num.value !== "" ? {invalidNumber: {value: num.value}} : null;
      }
    }

    let emptyFormControl = (default_value, additional_validators?) => {
      let validators = [];
      if(additional_validators) {
        validators.push(additional_validators);
      }

      return new UntypedFormControl(((default_value === undefined) ? '' : default_value), validators);
    }

    this.phonesForm = new UntypedFormGroup({
      primary_number: emptyFormControl(primary.call_number, validatePhoneNumber),
      primary_type: emptyFormControl(primary.type),
      primary_alerts_enabled: emptyFormControl(primary.enable_for_alerts),
      secondary_number: emptyFormControl(secondary.call_number, validatePhoneNumber),
      secondary_type: emptyFormControl(secondary.type || 'Mobile'),
      secondary_alerts_enabled: emptyFormControl(secondary.enable_for_alerts),
      verification_code: emptyFormControl('')
    })
  }

  private createEmailForm() {
    let email = new UntypedFormControl((this.selected_user && this.selected_user.email) || '', [Validators.required]);
    this.emailForm = new UntypedFormGroup({
      email: email,
      verification_code: new UntypedFormControl('')
    })
  }

  public kilnSecurityFormNetworkDevices() {
    if(this.kilnSecurityForm) {
      return (this.kilnSecurityForm.get('network_devices') as UntypedFormArray) ? (this.kilnSecurityForm.get('network_devices') as UntypedFormArray).controls : [];
    } else {
      return [];
    }
  }

  public getKilnsForNetworkDevice(index) {
    let nds = this.kilnSecurityFormNetworkDevices();
    if(nds.length > 0) {
      return (nds[index].get('kilns') as UntypedFormArray) ? (nds[index].get('kilns') as UntypedFormArray).controls : [];
    } else { return []; }
  }

  onNetworkDeviceViewChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_view')?.setValue(checked);
        });
      }
    }
  }

  onNetworkDeviceStopChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_stop')?.setValue(checked);
        });
      }
    }
  }

  onNetworkDeviceProgrammingChange(networkIndex: number, checked: boolean) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray) {
        kilnsArray.controls.forEach((kilnControl) => {
          kilnControl.get('can_program')?.setValue(checked);
        });
      }
    }
  }

  toggleCheckbox(event: Event, networkIndex: number, controlName: string, type: string) {
    // Prevent double-toggling if clicking directly on checkbox
    if ((event.target as HTMLElement).tagName === 'ION-CHECKBOX') {
      return;
    }
    
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let currentValue = networkDevice.get(controlName)?.value;
      networkDevice.get(controlName)?.setValue(!currentValue);
      
      // Trigger the cascade effect
      if(type === 'view') {
        this.onNetworkDeviceViewChange(networkIndex, !currentValue);
      } else if(type === 'stop') {
        this.onNetworkDeviceStopChange(networkIndex, !currentValue);
      } else if(type === 'programming') {
        this.onNetworkDeviceProgrammingChange(networkIndex, !currentValue);
      }
    }
  }

  toggleKilnCheckbox(event: Event, networkIndex: number, kilnIndex: number, controlName: string) {
    // Prevent double-toggling if clicking directly on checkbox
    if ((event.target as HTMLElement).tagName === 'ION-CHECKBOX') {
      return;
    }
    
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray && kilnsArray.controls[kilnIndex]) {
        let kilnControl = kilnsArray.controls[kilnIndex];
        let currentValue = kilnControl.get(controlName)?.value;
        kilnControl.get(controlName)?.setValue(!currentValue);
        
        // Update network device checkbox based on all kilns
        this.updateNetworkDeviceFromKilns(networkIndex, controlName);
      }
    }
  }

  onKilnCheckboxChange(networkIndex: number, controlName: string) {
    // Called when a kiln checkbox changes via ionChange
    this.updateNetworkDeviceFromKilns(networkIndex, controlName);
  }

  updateNetworkDeviceFromKilns(networkIndex: number, controlName: string) {
    let networkDevice = this.kilnSecurityFormNetworkDevices()[networkIndex];
    if(networkDevice) {
      let kilnsArray = networkDevice.get('kilns') as UntypedFormArray;
      if(kilnsArray && kilnsArray.controls.length > 0) {
        // Check if all kilns have this permission checked
        let allChecked = kilnsArray.controls.every((kilnControl) => {
          return kilnControl.get(controlName)?.value === true;
        });
        
        // Update network device checkbox to match
        networkDevice.get(controlName)?.setValue(allChecked);
      } else {
        // No kilns, so uncheck network device
        networkDevice.get(controlName)?.setValue(false);
      }
    }
  }

  private createKilnSecurityForm() {
    this.kilnSecurityForm = undefined;
    if(this.network_devices && this.kilns_by_network_device && this.selected_user) {
      let user_has_access_to_whole_account = (this.selected_user.network_devices && this.selected_user.network_devices.every((nd) => !nd.id)) && (this.selected_user.kilns && this.selected_user.kilns.every((k) => !k.id))
      // Get existing kiln IDs that user can stop (from saved user permissions)
      let existing_stop_kiln_ids = [];
      if(this.selected_user && this.selected_user.can_stop_kilns) {
        if(Array.isArray(this.selected_user.can_stop_kilns)) {
          existing_stop_kiln_ids = this.selected_user.can_stop_kilns.map((k) => {
            if(typeof k === 'object' && k.id) {
              return typeof k.id === 'string' ? parseInt(k.id) : k.id;
            } else {
              return typeof k === 'string' ? parseInt(k) : k;
            }
          }).filter((id) => !isNaN(id));
        }
      }

      // When user has no saved can_stop_kilns, default from API (can_stop on each kiln/ND from getDevicesForAccount)
      const useApiCanStop = existing_stop_kiln_ids.length === 0;

      // Get existing kiln IDs that user can program (from saved user permissions)
      let existing_program_kiln_ids: number[] = [];
      if (this.selected_user && this.selected_user.can_program_kilns && Array.isArray(this.selected_user.can_program_kilns)) {
        existing_program_kiln_ids = this.selected_user.can_program_kilns.map((k: { id?: number } | number) => {
          if (typeof k === 'object' && k && 'id' in k) return typeof k.id === 'string' ? parseInt(k.id) : k.id;
          return typeof k === 'string' ? parseInt(k) : k;
        }).filter((id: number) => !isNaN(id));
      }
      const useApiCanProgram = existing_program_kiln_ids.length === 0;
      const hasCanProgramFromApi = (k: { can_program?: boolean }) => !!(k as { can_program?: boolean }).can_program;

      // Create unified form structure with view, stop, and programming permissions
      let network_device_form_groups = this.network_devices.map((nd) => {
        if(nd) {
          let user_has_access_to_nd = this.selected_user && this.selected_user.network_devices && this.selected_user.network_devices.find((user_accessible_nd) => { return user_accessible_nd.id === nd.id })
          let kilns_to_map = this.kilns_by_network_device[nd.id] || [];
          
          // Check if all kilns under this network device can be viewed
          let all_kilns_can_view = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let user_has_access_to_kiln = this.selected_user && this.selected_user.kilns && this.selected_user.kilns.find((user_accessible_k) => { return user_accessible_k.id === k.id })
            return user_has_access_to_whole_account || (user_has_access_to_kiln !== undefined);
          });
          
          // Check if all kilns under this network device can be stopped (saved ids or API default)
          let all_kilns_can_stop = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
            if (useApiCanStop) return k.can_stop === true;
            return existing_stop_kiln_ids.includes(kilnId);
          });
          
          // Check if all kilns under this network device can be programmed
          let all_kilns_can_program = kilns_to_map.length > 0 && kilns_to_map.every((k) => {
            let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
            if (useApiCanProgram) return hasCanProgramFromApi(k);
            return existing_program_kiln_ids.includes(kilnId);
          });
          
          return new UntypedFormGroup({
            name: new UntypedFormControl(nd.name || `Name Unavailable (MAC: ${nd.mac_address})`),
            id: new UntypedFormControl(nd.id),
            can_view: new UntypedFormControl(user_has_access_to_whole_account || (user_has_access_to_nd !== undefined && all_kilns_can_view)),
            can_stop: new UntypedFormControl(useApiCanStop ? (nd.can_stop === true) : all_kilns_can_stop),
            can_program: new UntypedFormControl(useApiCanProgram ? hasCanProgramFromApi(nd) : all_kilns_can_program),
            kilns: new UntypedFormArray(kilns_to_map.map((k) => {
              let user_has_access_to_kiln = this.selected_user && this.selected_user.kilns && this.selected_user.kilns.find((user_accessible_k) => { return user_accessible_k.id === k.id })
              let kilnId = typeof k.id === 'string' ? parseInt(k.id) : k.id;
              let can_stop_kiln = useApiCanStop ? (k.can_stop === true) : existing_stop_kiln_ids.includes(kilnId);
              let can_program_kiln = useApiCanProgram ? hasCanProgramFromApi(k) : existing_program_kiln_ids.includes(kilnId);
              
              return new UntypedFormGroup({
                name: new UntypedFormControl(k.name),
                id: new UntypedFormControl(k.id),
                can_view: new UntypedFormControl(user_has_access_to_whole_account || (user_has_access_to_kiln !== undefined)),
                can_stop: new UntypedFormControl(can_stop_kiln),
                can_program: new UntypedFormControl(can_program_kiln)
              });
            }))
          })
        } else {
          return new UntypedFormGroup({
            name: new UntypedFormControl("Not Available"),
            id: new UntypedFormControl("na"),
            can_view: new UntypedFormControl(false),
            can_stop: new UntypedFormControl(false),
            can_program: new UntypedFormControl(false),
            kilns: new UntypedFormArray([])
          })
        }
      });

      this.kilnSecurityForm = new UntypedFormGroup({
        network_devices: new UntypedFormArray(network_device_form_groups)
      })
    } else {
      this.kilnSecurityForm = undefined;
    }
  }

  basicUsers() {
    if(!this.accountInformation) { return []; } else {
      if(this.canAdminister) {
        return this.accountInformation.users.filter((user) => { return user.type === 'basic' || !user.type });
      } else {
        return this.accountInformation.users.filter((user) => { return (user.email === this.loggedInUser || user.username === this.loggedInUser) && user.type === 'basic'});
      }
    }
  }

  admins() {
    return this.accountInformation ? this.accountInformation.users.filter((user) => { return user.type === 'admin'}) : [];
  }

  readOnlyUsers() {
    if(!this.accountInformation) { return []; } else {
      if(this.canAdminister) {
        return this.accountInformation.users.filter((user) => { return user.type === 'readonly' || !user.type });
      } else {
        return this.accountInformation.users.filter((user) => { return (user.email === this.loggedInUser || user.username === this.loggedInUser) && user.type === 'readonly'});
      }
    }
  }

  selectUser(user, index) {
    this.smallScreenAwaitingUserPick = false;
    this.selected_tab = 'details';
    this.selected_user = user;
    this.selected_index = index;
    this.createForm(this.selected_user)
    this.createPhonesForm();
    this.createEmailForm();
    this.createKilnSecurityForm();
    this.buttonText.emit({submitButton: "Create"});
  }

  getPhoneInformation(is_secondary = false) {
    let default_phone = {call_number: "", type: "Home"};
    let is_empty = (phone) => {
      return phone ? (!phone.carrier && !phone.type && !phone.call_number) : true;
    }

    if(this.selected_user) {
      let secondary = is_empty(this.selected_user.secondary_phone) ? default_phone : this.selected_user.secondary_phone;
      let primary = is_empty(this.selected_user.primary_phone) ? default_phone : this.selected_user.primary_phone;

      return is_secondary ? secondary : primary;
    } else {
      return default_phone;
    }
  }

  allowUsersChange(event) {
    this.allowUsers = event.target.value;
    if(this.allowUsers === 'all') {
      this.createKilnSecurityForm();
    }
  }

  isSelectedUserCurrentUser(): boolean {
    if (!this.selected_user) return false;
    const email = this.selected_user.email || this.selected_user.username;
    return !!email && (email === this.loggedInUser);
  }

  isLoggedInUser(user: { email?: string; username?: string }): boolean {
    if (!user || !this.loggedInUser) return false;
    const id = user.email || user.username;
    return !!id && id === this.loggedInUser;
  }

  getSelectedUserDisplayName(): string {
    if (!this.selected_user) return '';
    const name = [this.selected_user.first_name, this.selected_user.last_name].filter(Boolean).join(' ');
    const email = this.selected_user.email || this.selected_user.username || '';
    return name ? `${name} (${email})` : email;
  }

  openUserSelect(): void {
    if (this.userSelect && (!this.filteredUsers || this.filteredUsers.length === 0)) return;
    this.userSelect?.open();
  }

  get isPushAvailable(): boolean {
    console.log(`push available in user component: ${this.pushNotificationService.isPushAvailable}`)
    return this.pushNotificationService.isPushAvailable;
  }

  get currentPushDeviceName(): string | null {
    return this.pushNotificationService.getCurrentDeviceName();
  }

  /** Tab options for the small-screen dropdown (same visibility as segment buttons). */
  getAvailableTabs(): { value: string; labelKey: string }[] {
    const tabs: { value: string; labelKey: string }[] = [
      { value: 'details', labelKey: 'USERS.USER_DETAILS' },
    ];
    if (this.selected_user && this.canAdminister) {
      tabs.push({ value: 'security', labelKey: 'USERS.SECURITY' });
    }
    if (this.isPremiumAccount() && this.selected_user) {
      tabs.push({ value: 'phone', labelKey: 'USERS.PHONE' });
    }
    if (this.isPremiumAccount() && this.showPushNotifications && this.selected_user && (this.isSelectedUserCurrentUser() || this.canAdminister)) {
      tabs.push({ value: 'push', labelKey: 'USERS.PUSH' });
    }
    return tabs;
  }

  onTabDropdownChange(value: string) {
    this.selected_tab = value as 'details' | 'security' | 'phone' | 'push';
    if (value === 'push') {
      this.onPushTabSelected();
    }
  }

  onPushTabSelected() {
    this.selected_tab = 'push';
    if (this.isSelectedUserCurrentUser()) {
      this.pushDeviceName = this.currentPushDeviceName || '';
      this.loadOtherUserPushDevices();
    } else if (this.canAdminister && this.selected_user) {
      this.loadOtherUserPushDevices();
    }
  }

  loadOtherUserPushDevices() {
    const accountId = this.accountService.getCurrentAccount() ?? this.accountInformation?.general?.external_id;
    if (!accountId || !this.selected_user?.id) {
      this.otherUserPushDevices = [];
      this.canRegisterAdditionalPushDevice = false;
      return;
    }
    this.otherUserPushLoading = true;
    this.otherUserPushDevices = [];
    this.canRegisterAdditionalPushDevice = false;
    this.alertService.getPushTokensForUser(Number(accountId), this.selected_user.id)
      .then((list) => {
        this.otherUserPushDevices = Array.isArray(list) ? list : [];
        // For the current user, determine if this device can register an additional push token.
        if (this.isSelectedUserCurrentUser()) {
          const currentName = (this.currentPushDeviceName || '').trim();
          const hasCurrentByName = !!currentName && this.otherUserPushDevices.some((d) => (d.name || '').trim() === currentName);
          this.canRegisterAdditionalPushDevice = this.isPushAvailable && !hasCurrentByName;
        } else {
          this.canRegisterAdditionalPushDevice = false;
        }
      })
      .catch(() => {
        this.otherUserPushDevices = [];
        if (this.isSelectedUserCurrentUser()) {
          // If we couldn't load existing devices, still allow registration when push is available.
          this.canRegisterAdditionalPushDevice = this.isPushAvailable;
        } else {
          this.canRegisterAdditionalPushDevice = false;
        }
      })
      .finally(() => {
        this.otherUserPushLoading = false;
      });
  }

  async removePushDevice(device: { id: number; name?: string; platform?: string }) {
    if (!device || !device.id || !this.selected_user?.id) {
      return;
    }
    try {
      const res = await this.alertService.deletePushTokenById(device.id);
      const msg = (res && (res as { message?: string }).message) || 'Push device removed.';
      this.pending.emit({ is_pending: false, message: msg, success: true });
      this.showPushDeviceAlert(msg, true);
      this.loadOtherUserPushDevices();
    } catch (err: unknown) {
      const apiErrorMessage = this.getPushRegisterErrorMessage(err) || 'Failed to remove push device.';
      this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
      this.showPushDeviceAlert(apiErrorMessage, false);
    }
  }

  promptUserForPushNotifications() {
    const accountId = this.accountService.getCurrentAccount() ?? this.accountInformation?.general?.external_id;
    if (!accountId || !this.selected_user?.id) {
      this.pending.emit({ is_pending: false, message: 'Cannot send prompt.', success: false });
      return;
    }
    this.promptPushSending = true;
    this.alertService.promptUserForPush(Number(accountId), this.selected_user.email)
      .then((res) => {
        const msg = (res && (res as { message?: string }).message) || 'Prompt sent.';
        this.pending.emit({ is_pending: false, message: msg, success: true });
        this.showPushDeviceAlert(msg, true);
      })
      .catch((err: unknown) => {
        const apiErrorMessage = this.getPushRegisterErrorMessage(err) || 'Failed to send prompt.';
        this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
        this.showPushDeviceAlert(apiErrorMessage, false);
      })
      .finally(() => {
        this.promptPushSending = false;
      });
  }

  async registerPushDevice() {
    const name = (this.pushDeviceName || '').trim();
    if (!name) {
      this.pending.emit({ is_pending: false, message: 'Please enter a device name.', success: false });
      this.showPushDeviceAlert('Please enter a device name.', false);
      return;
    }
    if (!this.pushNotificationService.isPushAvailable) {
      this.pending.emit({ is_pending: false, message: 'Push notifications are not available on this device.', success: false });
      this.showPushDeviceAlert('Push notifications are not available on this device.', false);
      return;
    }

    this.pushRegistering = true;
    try {
      const result = await this.pushNotificationService.requestPermissionAndRegister();
      console.log(`registerPushDevice: result: ${JSON.stringify(result)}`)
      if (!result?.token) {
        this.pending.emit({ is_pending: false, message: 'Could not register for push notifications.', success: false });
        this.showPushDeviceAlert('Could not register for push notifications - no token received.', false);
        return;
      }
      try {
        let res = await this.alertService.registerPushToken(result.token, name);
        console.log(`registerPushDevice: res: ${JSON.stringify(res)}`)
        this.pushNotificationService.setCurrentDeviceName(name);
        this.pending.emit({ is_pending: false, message: 'Device registered for push notifications.', success: true });
        this.showPushDeviceAlert('Device registered for push notifications.', true);
      } catch (err: unknown) {
        const logPayload = this.getPushRegisterErrorLogPayload(err);
        console.error('*** registerPushToken error', JSON.stringify(logPayload));
        const apiErrorMessage = this.getPushRegisterErrorMessage(err);
        const status = logPayload.status;
        this.pending.emit({ is_pending: false, message: apiErrorMessage, success: false });
        this.showPushDeviceAlert(status != null ? `${apiErrorMessage} (Status: ${status})` : apiErrorMessage, false);
      }
    } finally {
      this.pushRegistering = false;
    }
  }

  /**
   * Build a plain object from a push register error for logging.
   * HttpErrorResponse stringifies as {} so we copy out status, error body, message explicitly.
   */
  private getPushRegisterErrorLogPayload(err: unknown): { status?: number; error?: unknown; message?: string } {
    console.log(`push err - getPushRegisterErrorLogPayload: ${JSON.stringify(err)}`)
    if (err == null || typeof err !== 'object') return { message: String(err) };
    const e = err as Record<string, unknown>;
    return {
      status: typeof e['status'] === 'number' ? e['status'] : undefined,
      error: e['error'],
      message: typeof e['message'] === 'string' ? e['message'] : undefined,
    };
  }

  /**
   * Extract a user-facing message from a push register API error.
   * HttpClient rejects with HttpErrorResponse: .error is the response body, .status is the code.
   * JSON.stringify(HttpErrorResponse) can be "{}" so we read properties directly.
   */
  private getPushRegisterErrorMessage(err: unknown): string {
    const fallback = 'Could not register device for push notifications.';
    if (err == null) return fallback;
    if (typeof err === 'object') {
      const e = err as Record<string, unknown>;
      const body = e['error'];
      if (body != null) {
        if (typeof body === 'string') return body || fallback;
        if (typeof body === 'object' && body !== null && typeof (body as Record<string, unknown>)['message'] === 'string') {
          return (body as { message: string }).message;
        }
      }
      if (typeof e['message'] === 'string' && e['message']) return e['message'] as string;
    }
    if (typeof err === 'string') return err || fallback;
    return fallback;
  }

  private async showPushDeviceAlert(message: string, success: boolean) {
    try {
      const alert = await this.alertController.create({
        header: 'Push notifications',
        message,
        buttons: ['OK']
      });
      await alert.present();
    } catch (e) {
      console.warn('Failed to present push device alert', e);
    }
  }

  public save() {
    if (this.smallScreenAwaitingUserPick) {
      return;
    }
    this.ensurePremiumTabVisibility();
    if (this.selected_tab === 'push') {
      return;
    }
    this.updatePending = true;
    if(this.selected_tab === 'details') {
      let formValue = this.usersForm.value
      formValue.enabled = true;
      if(formValue.email && this.selected_user_email && (formValue.email.trim() === this.selected_user_email.trim())) {
        delete formValue.email;
      } else {
        this.is_changing_email = true;
      }

      this.userService.premiumUser(formValue, this.selected_user, this.accountInformation.general.external_id)
          .then((response) => {
            if(this.selected_user) {
              if(this.is_changing_email && !this.userService.isLoggedInAsAnotherUser()) {
                this.userService.tryLogOut();
                this.kilnService.logOut();
                this.navCtrl.navigateForward('/');

                this.getUserAndDeviceInformation();
                this.pending.emit({
                  is_pending: false,
                  message: 'Your information was updated. Please log in with your updated email address.',
                  success: true,
                  dismiss: true
                });
              } else {
                this.updatePending = false;
                this.pending.emit({
                  is_pending: false,
                  message: 'User successfully updated.',
                  success: true
                });

                if(this.is_changing_email) {
                  this.gettingUserData = true;
                  this.getAccountUsers();
                }

                this.getUserAndDeviceInformation();
                let userIndex = this.accountInformation.users.findIndex((u) => { return (u.email === response.email || u.username === response.username) });
                this.accountInformation.users.splice(userIndex, 1, response);
                this.selectUser(response, userIndex);
              }
            } else {
              this.updatePending = false;
              this.pending.emit({
                is_pending: false,
                message: 'User successfully created.',
                success: true
              });

              this.getUserAndDeviceInformation();
              this.accountInformation.users.push(response);
              this.selectUser(response, (this.accountInformation.users.length - 1));
            }
          })
          .catch(() => {
            this.updatePending = false;
            if(this.selected_user) {
              this.pending.emit({
                is_pending: false,
                message: 'Sorry, we could not update the user at this time',
                success: false
              });
            } else {
              this.pending.emit({
                is_pending: false,
                message: 'Sorry, we could not create the user at this time',
                success: false
              });
            }
          })

    } else if(this.selected_tab === 'security' && this.canAdminister) {
      let formValue = this.kilnSecurityForm.value;
      let network_devices = [];
      let kilns = [];
      // Start from the user's can_stop_kilns array (from API), then apply form checkbox changes
      let can_stop_kiln_ids: number[] = [];
      if(this.selected_user && this.selected_user.can_stop_kilns && Array.isArray(this.selected_user.can_stop_kilns)) {
        can_stop_kiln_ids = this.selected_user.can_stop_kilns
          .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
          .filter((id) => !isNaN(id) && id > 0);
      }

      let can_program_kiln_ids: number[] = [];
      if(this.selected_user && this.selected_user.can_program_kilns && Array.isArray(this.selected_user.can_program_kilns)) {
        can_program_kiln_ids = this.selected_user.can_program_kilns
          .map((id) => (typeof id === 'string' ? parseInt(id, 10) : id))
          .filter((id) => !isNaN(id) && id > 0);
      }

      if(formValue.network_devices && Array.isArray(formValue.network_devices)) {
        formValue.network_devices.forEach((networkDevice) => {
          // Collect network devices for view access - validate ID is a positive number
          if(networkDevice.can_view && networkDevice.id) {
            let deviceId = typeof networkDevice.id === 'string' ? parseInt(networkDevice.id) : networkDevice.id;
            // Only add valid positive IDs (exclude -1, "na", 0, null, etc.)
            if(!isNaN(deviceId) && deviceId > 0) {
              network_devices.push(deviceId);
            }
          }

          // Collect kilns for view; apply form can_stop and can_program changes
          if(networkDevice.kilns && Array.isArray(networkDevice.kilns)) {
            networkDevice.kilns.forEach((kiln) => {
              if(kiln.can_view && kiln.id) {
                let kilnId = typeof kiln.id === 'string' ? parseInt(kiln.id) : kiln.id;
                // Only add valid positive IDs
                if(!isNaN(kilnId) && kilnId > 0) {
                  kilns.push(kilnId);
                }
              }
              const kilnId = typeof kiln.id === 'string' ? parseInt(kiln.id) : kiln.id;
              if(isNaN(kilnId) || kilnId <= 0) return;
              if(kiln.can_stop) {
                if(!can_stop_kiln_ids.includes(kilnId)) can_stop_kiln_ids.push(kilnId);
              } else {
                can_stop_kiln_ids = can_stop_kiln_ids.filter((id) => id !== kilnId);
              }
              if(kiln.can_program) {
                if(!can_program_kiln_ids.includes(kilnId)) can_program_kiln_ids.push(kilnId);
              } else {
                can_program_kiln_ids = can_program_kiln_ids.filter((id) => id !== kilnId);
              }
            });
          }
        });
      }
      
      // Submit network_devices, kilns, can_stop_kilns, and can_program_kilns
      let to_submit: Record<string, unknown> = {  
        network_devices: network_devices,      
        kilns: kilns,
        can_stop_kilns: can_stop_kiln_ids
      };
      if (this.showProgrammingToggle) {
        to_submit.can_program_kilns = can_program_kiln_ids;
      }
      
      this.userService.updateKilnSecurity((this.selected_user && this.selected_user.id), this.accountInformation.general.external_id, to_submit)
          .then((success) => {
            this.updatePending = false;
            console.log(`successfully updated user security`);
            this.pending.emit({is_pending: false, message: "User security updated successfully.", success: true});
            this.getUserAndDeviceInformation();
          })
          .catch((error) => {
            this.updatePending = false;
            console.log(`error updating user security`);
            console.log(error);
            this.pending.emit({is_pending: false,message: "Something went wrong updating the user's kiln access",success: false});
          });

    } else if(this.selected_tab === 'phone') {
      let formValue = this.phonesForm.value;
      let call_numbers = []
      if(formValue.primary_number !== "") {
        if(!formValue.primary_number.includes("+")) { formValue.primary_number = `+${formValue.primary_number}`}
        let primary_number = this.phoneUtil.parseAndKeepRawInput(formValue.primary_number, 'US');
        call_numbers.push({
          call_number: this.phoneUtil.format(primary_number, PhoneNumberFormat.INTERNATIONAL),
          type: formValue.primary_type,
          carrier: "",
          primary: true,
          secondary: false,
          enable_for_alerts: formValue.primary_alerts_enabled
        });
      }

      if(formValue.secondary_number !== "") {
        if(!formValue.secondary_number.includes("+")) { formValue.secondary_number = `+${formValue.secondary_number}`}
        let secondary_number = this.phoneUtil.parseAndKeepRawInput(formValue.secondary_number, 'US');
        call_numbers.push({
          call_number: this.phoneUtil.format(secondary_number, PhoneNumberFormat.INTERNATIONAL),
          type: formValue.secondary_type,
          carrier: "",
          primary: false,
          secondary: true,
          enable_for_alerts: formValue.secondary_alerts_enabled
        })
      }

      if(call_numbers.length === 0) {
        this.updatePending = false;
        this.pending.emit({
          is_pending: false,
          message: 'Please update a phone number before saving',
          success: false
        });
      } else {
        this.userService.updatePhone(this.selected_user, call_numbers)
            .then((response) => {
              this.updatePending = false;
              this.getUserAndDeviceInformation();
              this.accountInformation.users = this.accountInformation.users.map((u) => {
                if(u.username !== response.username && response.phones) {
                  u.primary_phone = response.phones.find((p) => !p.secondary)
                  u.secondary_phone = response.phones.find((p) => p.secondary)
                }

                return u;
              });
              this.pending.emit({is_pending: false, message: 'Phones updated successfully.', success: true});
            })
            .catch((error) => {
              this.updatePending = false;
              this.pending.emit({is_pending: false,message: 'Something went wrong updating phone numbers',success: false});
            });
      }
    } else if(this.selected_tab === 'email') {
      let formValue = this.usersForm.value;
      this.userService.updateEmail(this.selected_user, {email: formValue.email})
          .then((response) => {
            this.updatePending = false;
            this.getUserAndDeviceInformation();
            this.pending.emit({is_pending: false, message: 'Email updated successfully.', success: true});
            this.accountInformation.users = this.accountInformation.users.map((u) => {
              if(u.username !== response.username) { u.email = response.email; }
              return u;
            });
          })
          .catch((error) => {
            this.updatePending = false;
            this.pending.emit({is_pending: false,message: 'Something went wrong updating the user email',success: false});
          });
    }
  }

  public addUser() {
    this.smallScreenAwaitingUserPick = false;
    this.selected_user = null;
    this.selected_index = null;
    this.selected_tab = 'details';
    this.createForm();
    this.createPhonesForm();
    this.createEmailForm();
    this.buttonText.emit({submitButton: "Create"});
  }

  async removeUser() {
    let username = this.selected_user.email || this.selected_user.username || "this user";
    const alert = await this.alertController.create({
      header: 'Delete confirmation',
      message: `Are you sure you want to remove ${username} from this account? This action cannot be reversed.`,
      buttons: [
        {
          text: 'Yes, Delete',
          role: 'delete',
          cssClass: 'primary',
          handler: (data) => {
            this.pending.emit({is_pending: true});
            this.userService.removeUserFromAccount(this.accountInformation.general.external_id, this.selected_user.id)
                .then((response) => {
                  this.pending.emit({
                    is_pending: false,
                    message: 'User successfully removed.',
                    success: true
                  });

                  this.accountInformation.users = this.accountInformation.users.filter((u) => { return u.id !== response.removed});
                  this.selected_user = undefined;
                  this.clearCurrentForm();                  
                })
          }
        }, {
          text: "No, Cancel",
          cssClass: 'secondary',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  deletePhone(secondary) {
    let phone = secondary ? (this.selected_user.secondary_phone && this.selected_user.secondary_phone.call_number) : (this.selected_user.primary_phone && this.selected_user.primary_phone.call_number);
    this.alertService.deletePhone(phone)
        .then((success) => {
          this.getAccountUsers();
          this.pending.emit({is_pending: false,message: "The phone number was deleted",success: true});
        })
        .catch((err) => {
          this.pending.emit({is_pending: false,message: "Something went wrong deleting your phone number",success: false});
        })
  }

  verifyPhone(secondary) {
    let val = this.phonesForm.value;
    let call_number = secondary ? val.secondary_number : val.primary_number;
    this.alertService.verifyDevice(this.selected_user, "phone", {phone: call_number})
        .then((response) => {this.verificationSuccess() })
        .catch((error) => { this.verificationFails() });
  }

  confirmPhoneVerification(secondary) {
    let val = this.phonesForm.value;
    let call_number = secondary ? val.secondary_number : val.primary_number;
    this.alertService.confirmVerification("phone", {phone: call_number, verification_code: val.verification_code})
        .then((response) => { this.deviceConfirmSuccess() })
        .catch((error) => { this.deviceConfirmFails() })
  }

  verifyEmail() {
    let val = this.usersForm.value;
    this.alertService.verifyDevice(this.selected_user, "email", {email: val.email})
        .then((response) => { this.verificationSuccess() })
        .catch((error) => { this.verificationFails() })
  }

  confirmEmailVerification() {
    let val = this.usersForm.value;
    this.alertService.confirmVerification("email", {email: val.email, verification_code: val.verification_code})
        .then((response) => { this.deviceConfirmSuccess() })
        .catch((error) => { this.deviceConfirmFails() })
  }

  public pickerButtons = [
    {
      text: 'Cancel',
      role: 'cancel',
    },
    {
      text: 'Confirm',
      handler: (selection) => {
        this.selectUser(selection.users.value.user, selection.users.value.index);
      },
    },
  ];

  onUserSelectChange() {
    if (this.filteredUsers?.length && this.selected_index >= 0 && this.selected_index < this.filteredUsers.length) {
      this.selectUser(this.filteredUsers[this.selected_index], this.selected_index);
    }
  }

  async showPicker() {
    let picker = await this.pickerController.create({
      columns: [
      {
        name: 'users',
        options: this.accountInformation.users.sort((u1, u2) => {
          let val = 0;
          if((u1.type === 'admin' && u2.type !== 'admin') || (u1.type === 'basic' && u2.type === 'readonly')) val = -1;
          if(u1.type === 'readonly') val = 1;
          return val;
        }).map((user, i) => { 
          let typeLabels = {'admin': "Admin", 'basic': "Basic", 'readonly': "Readonly"}
          return {          
            text: `${user.email} - ${typeLabels[user.type]}`, value: {user: user, index: i}
          }})
      }],
      buttons: this.pickerButtons
    });

    await picker.present();
  }

  public isValid() {
    if (this.small_screen && this.smallScreenAwaitingUserPick) {
      return false;
    }
    if (this.selected_tab === 'push') {
      return false;
    }
    if(this.selected_tab) {
      let getFormToCheck = {
        "details": this.usersForm,
        "phone": this.phonesForm,
        "email": this.emailForm,
        "security": this.kilnSecurityForm
      }

      let currentForm = getFormToCheck[this.selected_tab];
      return (currentForm && currentForm.touched) ? currentForm.valid : true;
    } else {
      return true;
    }
  }

  get filteredUsers() {
    if (!this.accountInformation || !this.accountInformation.users) return [];
    switch (this.selectedRole) {
      case 'admin':
        return this.accountInformation.users.filter(u => u.type === 'admin');
      case 'basic':
        return this.accountInformation.users.filter(u => u.type === 'basic');
      case 'readonly':
        return this.accountInformation.users.filter(u => u.type === 'readonly');
      default:
        return this.accountInformation.users;
    }
  }
}
